import { useAuth } from "@/hooks/use-auth-simple";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertUserSchema } from "@shared/schema";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Redirect } from "wouter";
import { Home, Lock, User, Shield, Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";
import { GrokLogo } from "@/components/grok-logo";
import { useTheme } from "next-themes";
import { LeaderboardPreview } from "@/components/leaderboard-preview";

export default function AuthPage() {
  const { user, loginMutation } = useAuth();
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && theme === 'dark';

  const loginForm = useForm({
    resolver: zodResolver(insertUserSchema),
    mode: "onSubmit",
    defaultValues: {
      username: "",
      password: "",
    },
  });



  if (user) {
    return <Redirect to="/" />;
  }

  const onLogin = (data: any) => {
    loginMutation.mutate(data);
  };



  return (
    <div 
      className={`min-h-screen flex ${isDark ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`} 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* GROK-Style Header - Minimal Top Bar */}
      <header 
        className={`absolute top-0 left-0 right-0 px-4 sm:px-6 h-14 border-b backdrop-blur-xl z-50 ${isDark ? 'border-white/10 bg-black/50' : 'border-gray-200 bg-white/80'}`}
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="flex items-center h-full max-w-full mx-auto w-full">
          <GrokLogo size={32} className="flex-shrink-0" />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center pt-14 px-4 sm:px-8 pb-8 w-full">
        <div className="w-full max-w-md" style={{ textAlign: 'left' }}>

          {/* Header Section - Logo with Dissolve Animation */}
          <div className="text-center mb-8 relative">
            <div className="flex items-center justify-center animate-logo-dissolve">
              <GrokLogo 
                size={120} 
                className="flex-shrink-0 drop-shadow-lg"
              />
            </div>
          </div>

          {/* Login Card - GROK Style */}
          <Card className={`relative shadow-2xl rounded-2xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`} style={{
            background: isDark ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: isDark ? '0 4px 6px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)' : '0 4px 6px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)'
          }}>

            <CardContent className="relative z-10 pt-6 px-8 pb-4">

              {/* Login Form Header */}
              <div className="mb-6">
                <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Sign In</h2>
                <p className={`text-sm ${isDark ? 'text-[#808080]' : 'text-gray-600'}`}>Access Chat TLS and manage your mortgage resources</p>
              </div>

              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(onLogin)} noValidate className="space-y-4">
                      <FormField
                        control={loginForm.control}
                        name="username"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={`font-medium text-sm mb-2 block ${isDark ? 'text-white' : 'text-gray-900'}`}>The Loan Store Email</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <User className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10 ${isDark ? 'text-[#808080]' : 'text-gray-400'}`} />
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="Enter your The Loan Store email"
                                  className={`pl-10 h-11 rounded-lg transition-all duration-200 ${isDark ? 'text-white bg-black/50 border-white/10 placeholder:text-[#808080] focus:border-white/20' : 'text-gray-900 bg-white border-gray-200 placeholder:text-gray-400 focus:border-gray-300'} focus:ring-0`}
                                  style={{
                                    backdropFilter: 'blur(10px)',
                                    fontSize: '16px',
                                    textAlign: 'left',
                                    WebkitAppearance: 'none',
                                    appearance: 'none',
                                    lineHeight: '1.5'
                                  }}
                                  data-testid="input-login-username"
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-[#1a1a1a] text-sm mt-1" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={loginForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={`font-medium text-sm mb-2 block ${isDark ? 'text-white' : 'text-gray-900'}`}>Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10 ${isDark ? 'text-[#808080]' : 'text-gray-400'}`} />
                                <Input
                                  {...field}
                                  type={showLoginPassword ? "text" : "password"}
                                  placeholder="Enter your password"
                                  className={`pl-10 pr-10 h-11 rounded-lg transition-all duration-200 ${isDark ? 'text-white bg-black/50 border-white/10 placeholder:text-[#808080] focus:border-white/20' : 'text-gray-900 bg-white border-gray-200 placeholder:text-gray-400 focus:border-gray-300'} focus:ring-0`}
                                  style={{
                                    backdropFilter: 'blur(10px)',
                                    fontSize: '16px',
                                    textAlign: 'left',
                                    WebkitAppearance: 'none',
                                    appearance: 'none',
                                    lineHeight: '1.5'
                                  }}
                                  data-testid="input-login-password"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                                  className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors focus:outline-none z-10 ${isDark ? 'text-[#808080] hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                                  aria-label={showLoginPassword ? "Hide password" : "Show password"}
                                >
                                  {showLoginPassword ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage className="text-[#1a1a1a] text-sm mt-1" />
                          </FormItem>
                        )}
                      />

                      {/* Forgot Password Link */}
                      <div className="flex justify-end -mt-2">
                        <button
                          type="button"
                          className={`text-sm font-medium transition-colors duration-200 hover:underline ${isDark ? 'text-[#E0E0E0] hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                          onClick={() => {
                            // TODO: Implement forgot password functionality
                            alert("Forgot password functionality coming soon");
                          }}
                        >
                          Forgot Password?
                        </button>
                      </div>

                  <Button
                    type="submit"
                    className={`w-full h-12 text-base font-semibold rounded-lg transition-all duration-200 shadow-lg mt-2 animate-button-press touch-manipulation ${isDark ? 'bg-[#6B7280] hover:bg-[#4B5563] text-white shadow-gray-500/20' : 'bg-gray-700 hover:bg-gray-800 text-white shadow-gray-400/20'}`}
                    style={{ 
                      minHeight: '48px',
                      fontSize: '16px',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    disabled={loginMutation.isPending}
                    data-testid="button-login"
                  >
                    {loginMutation.isPending ? "Signing in..." : "Sign In"}
                  </Button>

                  {/* Request Account Link */}
                  <div className="text-center pt-4">
                    <p className={`text-sm ${isDark ? 'text-[#808080]' : 'text-gray-600'}`}>
                      Need access?{' '}
                      <button
                        type="button"
                        className={`hover:underline font-semibold transition-colors duration-200 ${isDark ? 'text-[#E0E0E0] hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                        onClick={() => {
                          // TODO: Implement request account functionality
                          alert("Request account functionality coming soon");
                        }}
                      >
                        Request Account
                      </button>
                    </p>
                  </div>
                </form>
              </Form>

              {/* Footer */}
              <div className={`mt-6 pt-4 border-t text-center ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <p className={`text-xs font-medium ${isDark ? 'text-[#808080]' : 'text-gray-600'}`}>Secure access for authorized TLS employees only</p>
                <p className={`text-xs mt-2 ${isDark ? 'text-[#808080]' : 'text-gray-600'}`}>NMLS #1121650</p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>

      <div className={`hidden lg:flex flex-1 items-center justify-center pt-14 px-12 pb-12 relative ${isDark ? 'bg-black' : 'bg-gray-100'}`}>
        <div className={`relative z-10 max-w-md space-y-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <div className={`relative animate-fade-in-delay-3 rounded-2xl p-8 border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`} style={{
            background: isDark ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: isDark ? '0 4px 6px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)' : '0 4px 6px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)'
          }}>
            <div className="relative z-10">
              <h1 className={`text-2xl font-bold mb-4 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>Welcome to Chat TLS</h1>
              <p className={`text-base leading-relaxed ${isDark ? 'text-[#E0E0E0]' : 'text-gray-700'}`}>
                Your AI-powered mortgage assistant for The Loan Store. Get instant answers about loan products, 
                guidelines, and processes.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div className={`relative flex items-start gap-4 rounded-2xl p-6 border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`} style={{
              background: isDark ? 'rgba(26,26,26,0.8)' : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)'
            }}>
              <div className={`relative z-10 w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#6B7280]' : 'bg-gray-600'}`}>
                <span className="text-xl font-bold text-white">✓</span>
              </div>
              <div className="relative z-10 flex-1">
                <h3 className={`font-semibold text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Expert Guidance</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-[#808080]' : 'text-gray-600'}`}>Get answers about Non-QM, DSCR, conventional loans, and more</p>
              </div>
            </div>
            <div className={`relative flex items-start gap-4 rounded-2xl p-6 border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`} style={{
              background: isDark ? 'rgba(26,26,26,0.8)' : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)'
            }}>
              <div className={`relative z-10 w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#6B7280]' : 'bg-gray-600'}`}>
                <span className="text-xl font-bold text-white">✓</span>
              </div>
              <div className="relative z-10 flex-1">
                <h3 className={`font-semibold text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>24/7 Availability</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-[#808080]' : 'text-gray-600'}`}>Access information anytime, day or night</p>
              </div>
            </div>
            <div className={`relative flex items-start gap-4 rounded-2xl p-6 border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`} style={{
              background: isDark ? 'rgba(26,26,26,0.8)' : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)'
            }}>
              <div className={`relative z-10 w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#6B7280]' : 'bg-gray-600'}`}>
                <span className="text-xl font-bold text-white">✓</span>
              </div>
              <div className="relative z-10 flex-1">
                <h3 className={`font-semibold text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Secure & Private</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-[#808080]' : 'text-gray-600'}`}>Your conversations are protected and confidential</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Leaderboard Preview */}
        <LeaderboardPreview />
      </div>
    </div>
  );
}
